"use client";

import React, { createContext, useContext, useMemo } from "react";

export interface PageContextValue<TPage, TSections = unknown> {
  pageData: TPage;
  normalizedSections: TSections;
}

interface PageContextProviderProps<TPage, TSections> {
  value: { pageData: TPage };
  normalizer?: (sections: TPage extends { sections: infer S } ? S : unknown) => TSections;
  children: React.ReactNode;
}

const PageContext = createContext<PageContextValue<unknown> | null>(null);

// 🧩 Provider that can apply a custom normalizer
export function PageContextProvider<TPage, TSections>({
  value,
  normalizer,
  children,
}: PageContextProviderProps<TPage, TSections>) {
  const normalizedSections = useMemo(() => {
    if (!normalizer) return {} as TSections;
    const sections = (value.pageData as TPage & { sections?: unknown }).sections;
    return sections
      ? normalizer(sections as TPage extends { sections: infer S } ? S : unknown)
      : ({} as TSections);
  }, [value.pageData, normalizer]);

  const contextValue: PageContextValue<TPage, TSections> = {
    ...value,
    normalizedSections,
  };

  return <PageContext.Provider value={contextValue}>{children}</PageContext.Provider>;
}

// 🔍 Typed hook
export function usePageContext<TPage, TSections = unknown>() {
  const context = useContext(PageContext);
  if (!context) throw new Error("usePageContext must be used within a PageContextProvider");
  return context as PageContextValue<TPage, TSections>;
}
